import _ from 'lodash';
import React from 'react';

import {
  Check,
  Error,
  NetworkPing,
  PlayArrow,
  Refresh
} from '@mui/icons-material';
import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import {
  BaseWidget,
  SpinningInProgressIndicator,
  tableUtil
} from 'modules';
import {
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

import * as dataService from './dataService';




const StyledTableCell = styled(TableCell)({
  fontSize: 12
});



export const FC: React.FC = () => {
  const queryTuple = dataService.useQuery();
  const [testPing, testPingTuple] = dataService.useTestPing();
  const [runScheduledJob] = dataService.useRunScheduledJob();
  const [toggleScheduledJob] = dataService.useToggleScheduledJob();
  const [searchText, setSearchText] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [tick, setTick] = React.useState(false);
  const [pingE1, pingE1Tuple] = dataService.usePingE1();
  const [pingNetSuite, pingNetSuiteTuple] = dataService.usePingNetSuite();



  React.useEffect(
    () => {
      const interval = setInterval(() => {
        setTick(p => !p);
      }, 1000);

      return () => clearInterval(interval);
    },
    []
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      queryTuple.refetch();
    }, 1000 * 60);
    return () => clearInterval(interval);
  }, [queryTuple]);



  function handleClickTestPing() {
    testPing();
  }



  const handleClickRunJob = React.useCallback(
    (scheduledJobId: string) => {
      const job = _.find(queryTuple.data.scheduledJobs, job => job.id === scheduledJobId);
      const daemonName = job?.daemonDisplayName;
      if (daemonName && window.confirm(`Are you sure you want to run ${daemonName}?`)) {
        runScheduledJob(scheduledJobId);
      }
    },
    [
      runScheduledJob,
      queryTuple.data.scheduledJobs
    ]
  );



  const handleClickRefresh = React.useCallback(
    () => {
      queryTuple.refetch();
    },
    [queryTuple]
  );



  const filteredJobs = React.useMemo(
    () => {
      if (searchText) {
        const loweredSearchText = _.toLower(searchText);
        return _.filter(
          queryTuple.data.scheduledJobs,
          job => _.includes(_.toLower(job.daemonDisplayName), loweredSearchText)
        );

      } else if (checked) {
        return queryTuple.data.scheduledJobs.filter(job => job.repeatPeriod < job.averageRunTime);

      } else {
        return queryTuple.data.scheduledJobs;
      }
    },
    [
      queryTuple.data.scheduledJobs,
      checked,
      searchText
    ]);



  return (
    <>
      <Grid
        container
        spacing={1}>
        <Grid
          item
          xs={8}>
          <BaseWidget.FC
            title="Scheduled Jobs"
            elAction={(
              <Stack
                direction="row"
                gap={1}>
                <Button
                  variant="contained"
                  disabled={pingE1Tuple.loading}
                  onClick={() => pingE1()}>
                  E1

                  {pingE1Tuple.loading && (
                    <SpinningInProgressIndicator.FC />
                  )}

                  {(pingE1Tuple.data !== 'pending' && !pingE1Tuple.loading) && (
                    pingE1Tuple.data === 'success' ? <Check color="success" /> : <Error color="error" />
                  )}
                </Button>

                <Button
                  variant="contained"
                  disabled={pingNetSuiteTuple.loading}
                  onClick={() => pingNetSuite()}>
                  NS {(pingNetSuiteTuple.data !== 'pending' && !pingNetSuiteTuple.loading) && (
                    pingNetSuiteTuple.data === 'success' ? <Check color="success" /> : <Error color="error" />
                  )}

                  {pingNetSuiteTuple.loading && (
                    <SpinningInProgressIndicator.FC />
                  )}
                </Button>

                <IconButton
                  disabled={testPingTuple.loading}
                  onClick={handleClickTestPing}>
                  {testPingTuple.loading ? <SpinningInProgressIndicator.FC /> : <NetworkPing />}
                </IconButton>

                <IconButton
                  onClick={handleClickRefresh}>
                  <Refresh />
                </IconButton>
              </Stack>
            )}>

            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                value={searchText}
                onChange={ev => setSearchText(ev.currentTarget.value)} />

              <FormControlLabel
                control={(
                  <Switch
                    checked={checked}
                    onChange={() => { setChecked(!checked); }} />
                )}
                label="With time overruns" />
            </Stack>

            <Table
              size="small"
              sx={{
                fontSize: 10
              }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    Daemon
                  </StyledTableCell>

                  <StyledTableCell>
                    Window
                  </StyledTableCell>

                  <StyledTableCell>
                    Interval
                  </StyledTableCell>

                  <StyledTableCell>
                    Avg. Run Time
                  </StyledTableCell>

                  <StyledTableCell>
                    Time Since Last Run
                  </StyledTableCell>

                  <StyledTableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {queryTuple.loading ? tableUtil.makeSkeletonRows(5, 10) : filteredJobs.map(job => (
                  <TableRow key={job.id}>
                    <StyledTableCell>{job.daemonDisplayName}</StyledTableCell>
                    {job.windowStartTime !== 'All Day' ? (
                      <StyledTableCell>{job.windowStartTime} - {job.windowEndTime}</StyledTableCell>
                    ) : (
                      <StyledTableCell>-</StyledTableCell>
                    )}

                    <StyledTableCell>{job.repeatPeriod.toFormat(`m'm'`)}</StyledTableCell>
                    <StyledTableCell>{job.averageRunTime.toFormat(`hh'h' mm'm' ss's'`)}</StyledTableCell>
                    <StyledTableCell>{job.lastRunTimestamp?.diffNow().negate()?.toFormat(`hh'h' mm'm' ss's'`) ?? '-'}</StyledTableCell>
                    <StyledTableCell>
                      <IconButton
                        size="small"
                        color={job.isRunning ? 'default' : 'success'}
                        disabled={job.isRunning}
                        onClick={_.partial(handleClickRunJob, job.id)}>
                        {!job.isRunning ? <PlayArrow /> : <SpinningInProgressIndicator.FC />}
                      </IconButton>

                      <Switch
                        size="small"
                        checked={job.isActive}
                        onChange={() => toggleScheduledJob(job.id, !job.isActive)} />
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </BaseWidget.FC>
        </Grid>

        <Grid
          item
          xs={4}>
          <BaseWidget.FC>
            <Stack spacing={3}>
              <ResponsiveContainer height={384}>
                <PieChart>
                  <Pie
                    dataKey="y"
                    data={queryTuple.data.scheduledJobRunTime}
                    paddingAngle={2}
                    innerRadius={60}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, index }) => {
                      const radius = innerRadius + (outerRadius) * 0.75;
                      const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                      const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
                      return (
                        <>
                          <text
                            fontSize={8}
                            x={x}
                            y={y}
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central">
                            {queryTuple.data.scheduledJobRunTime[index].x}
                          </text>
                        </>
                      );
                    }} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Stack>
          </BaseWidget.FC>
        </Grid>

      </Grid >
    </>
  );
};
